import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trophy, Crown, Medal, Star, Share2, Copy, Users, Gift, 
  ChevronRight, ChevronDown, Sparkles, TrendingUp, Award, Target, Zap,
  MessageCircle, Pencil, X, Check, Loader2, Rocket, Satellite
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { api } from '../services/api';
import { useTheme } from './ThemeToggle';

// ============================================
// 🚀 SYSTÈME DE RANGS GALACTIQUES
// ============================================

const REFERRAL_TIERS = [
  { id: 'starter', name: 'Recrue', min: 0, max: 4, icon: '🌱', color: '#6B7280', benefit: '+1 tampon par filleul' },
  { id: 'bronze', name: 'Cadet', min: 5, max: 9, icon: '🥉', color: '#CD7F32', benefit: '+ Cadeau surprise' },
  { id: 'silver', name: 'Lieutenant', min: 10, max: 19, icon: '🥈', color: '#C0C0C0', benefit: '+ Double tampons 1 semaine' },
  { id: 'gold', name: 'Commandant', min: 20, max: 49, icon: '🥇', color: '#FFD700', benefit: '+ Cadeau premium' },
  { id: 'diamond', name: 'Amiral', min: 50, max: Infinity, icon: '💎', color: '#00FFFF', benefit: '+ Accès VIP exclusif' }
];

const getTier = (count) => REFERRAL_TIERS.find(tier => count >= tier.min && count <= tier.max) || REFERRAL_TIERS[0];
const getNextTier = (count) => {
  const idx = REFERRAL_TIERS.findIndex(tier => count >= tier.min && count <= tier.max);
  return idx < REFERRAL_TIERS.length - 1 ? REFERRAL_TIERS[idx + 1] : null;
};

// ============================================
// 🎯 COMPOSANT PRINCIPAL
// ============================================

export function ReferralShowcase({ customer, onUpdate }) {
  const [topReferrers, setTopReferrers] = useState([]);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { isDarkMode } = useTheme();

  const referralCount = customer?.referralCount || 0;
  const referralCode = customer?.referralCode || '';
  const referralCodeCustomized = customer?.referralCodeCustomized || false;
  const currentTier = getTier(referralCount);
  const nextTier = getNextTier(referralCount);
  const progressToNext = nextTier ? ((referralCount - currentTier.min) / (nextTier.min - currentTier.min)) * 100 : 100;

  useEffect(() => {
    let isMounted = true;
    api.getTopReferrers().then(r => isMounted && setTopReferrers(r.topReferrers || [])).catch(() => {
      if (isMounted) setTopReferrers([
        { name: 'Sophie', referralCount: 12 },
        { name: 'Marc', referralCount: 8 },
        { name: 'Julie', referralCount: 6 },
        { name: 'Lucas', referralCount: 5 },
        { name: 'Emma', referralCount: 4 },
      ]);
    });
    return () => { isMounted = false; };
  }, []);

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`🚀 Rejoins La Petite Épicerie avec mon code "${referralCode}" ! On gagne tous les deux un tampon gratuit ! 🎁`)}`, '_blank');
    toast.success('WhatsApp ouvert !');
  };

  const shareSMS = () => window.open(`sms:?body=${encodeURIComponent(`🚀 Rejoins LPE avec mon code "${referralCode}" et gagne un tampon gratuit ! 🎁`)}`, '_blank');

  const shareSnap = () => {
    navigator.clipboard.writeText(`🚀 Code parrain LPE : ${referralCode} - On gagne tous les deux ! 🎁`);
    toast.success('Copié ! Colle dans Snap 👻');
    setTimeout(() => { window.location.href = 'snapchat://'; }, 500);
  };

  const shareInsta = () => {
    navigator.clipboard.writeText(`🚀 Code parrain LPE : ${referralCode} - On gagne tous les deux ! 🎁`);
    toast.success('Copié ! Colle dans Insta 📸');
    setTimeout(() => { window.location.href = 'instagram://direct-inbox'; }, 500);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Code copié ! 📋');
  };

  const handleCustomizeCode = async () => {
    if (!newCode.trim() || newCode.length < 4) { toast.error('Min 4 caractères !'); return; }
    setIsCustomizing(true);
    try {
      const result = await api.customizeReferralCode(customer.id, newCode.trim());
      toast.success('Code personnalisé ! 🎉');
      setShowCustomizeModal(false);
      setNewCode('');
      if (onUpdate) onUpdate({ ...customer, referralCode: result.referralCode, referralCodeCustomized: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setIsCustomizing(false);
    }
  };

  return (
    <div className="space-y-0 relative">
      {/* HEADER ACCORDÉON */}
      <motion.button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full relative overflow-hidden rounded-2xl cursor-pointer"
        style={{
          background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #1e3a5f 100%)',
          boxShadow: '0 0 40px rgba(6, 182, 212, 0.2), 0 15px 30px rgba(0,0,0,0.4)',
          border: '1px solid rgba(6, 182, 212, 0.3)'
        }}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        {/* Étoiles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-cyan-400"
              style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.3 + 0.1 }}
              animate={{ opacity: [0.1, 0.5, 0.1] }}
              transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
            />
          ))}
        </div>

        {/* HUD Corners */}
        <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-cyan-500/50" />
        <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-cyan-500/50" />

        <div className="relative p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <motion.div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(6, 182, 212, 0.1) 100%)', border: '1px solid rgba(6, 182, 212, 0.3)' }}
                animate={{ boxShadow: ['0 0 10px rgba(6, 182, 212, 0.2)', '0 0 25px rgba(6, 182, 212, 0.4)', '0 0 10px rgba(6, 182, 212, 0.2)'] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Rocket className="w-6 h-6 text-cyan-400" />
              </motion.div>
              <div className="text-left">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  🚀 Recrutement Galactique
                  {referralCount > 0 && (
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-green-500/20 text-green-400 border border-green-500/30">
                      {referralCount} RECRUE{referralCount > 1 ? 'S' : ''}
                    </span>
                  )}
                </h3>
                <p className="text-[11px] text-cyan-500/70 font-mono tracking-wider">
                  PROGRAMME PARRAINAGE • RANG: {currentTier.name.toUpperCase()}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <span className="text-2xl">{currentTier.icon}</span>
              <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.3 }}>
                <ChevronDown className="w-5 h-5 text-cyan-400" />
              </motion.div>
            </div>
          </div>
        </div>
      </motion.button>

      {/* CONTENU DÉPLIABLE */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="space-y-4 pt-4">

              {/* RANG & PROGRESSION */}
              <motion.div
                className="relative overflow-hidden rounded-2xl p-4"
                style={{
                  background: 'linear-gradient(135deg, #020617 0%, #0f172a 100%)',
                  border: `2px solid ${currentTier.color}40`
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <motion.span className="text-4xl" animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                      {currentTier.icon}
                    </motion.span>
                    <div>
                      <p className="text-xs text-gray-500 font-mono">RANG ACTUEL</p>
                      <p className="text-xl font-black" style={{ color: currentTier.color }}>{currentTier.name.toUpperCase()}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500 font-mono">RECRUES</p>
                    <motion.p className="text-3xl font-black text-white" key={referralCount} initial={{ scale: 1.5 }} animate={{ scale: 1 }}>
                      {referralCount}
                    </motion.p>
                  </div>
                </div>

                {nextTier && (
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-500">Prochain rang : {nextTier.icon} {nextTier.name}</span>
                      <span style={{ color: nextTier.color }}>{nextTier.min - referralCount} recrues restantes</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden bg-gray-800">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: `linear-gradient(90deg, ${currentTier.color}, ${nextTier.color})`, boxShadow: `0 0 10px ${currentTier.color}` }}
                        initial={{ width: 0 }}
                        animate={{ width: `${progressToNext}%` }}
                        transition={{ duration: 1 }}
                      />
                    </div>
                  </div>
                )}

                <div className="mt-3 text-center py-2 px-3 rounded-lg text-sm" style={{ background: `${currentTier.color}20`, color: currentTier.color }}>
                  <Gift className="w-4 h-4 inline mr-2" />
                  {currentTier.benefit}
                </div>
              </motion.div>

              {/* CODE PARRAIN */}
              <motion.div
                className="relative overflow-hidden rounded-2xl p-4"
                style={{
                  background: 'linear-gradient(135deg, #020617 0%, #064e3b 100%)',
                  border: '1px solid rgba(34, 197, 94, 0.3)'
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div className="text-center mb-4">
                  <p className="text-xs text-gray-500 font-mono mb-1">CODE DE RECRUTEMENT</p>
                  <motion.p 
                    className="text-3xl font-black text-green-400 tracking-widest font-mono"
                    style={{ textShadow: '0 0 20px rgba(34, 197, 94, 0.5)' }}
                    whileHover={{ scale: 1.05 }}
                  >
                    {referralCode}
                  </motion.p>
                  <p className="text-xs text-gray-500 mt-1">Partage-le et gagnez tous les deux !</p>
                  
                  {!referralCodeCustomized && (
                    <button
                      onClick={() => setShowCustomizeModal(true)}
                      className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 transition-colors"
                    >
                      <Pencil className="w-3 h-3" /> Personnaliser
                    </button>
                  )}
                </div>

                {/* Boutons de partage */}
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { fn: shareWhatsApp, icon: <MessageCircle className="w-5 h-5" />, label: 'WhatsApp', bg: 'linear-gradient(135deg, #25D366, #128C7E)' },
                    { fn: shareSMS, icon: <MessageCircle className="w-5 h-5" />, label: 'SMS', bg: 'linear-gradient(135deg, #34C759, #30B350)' },
                    { fn: shareSnap, icon: '👻', label: 'Snap', bg: 'linear-gradient(135deg, #FFFC00, #FFE500)', textDark: true },
                    { fn: shareInsta, icon: '📸', label: 'Insta', bg: 'linear-gradient(135deg, #F58529, #DD2A7B, #8134AF)' },
                    { fn: copyCode, icon: <Copy className="w-5 h-5" />, label: 'Copier', bg: 'linear-gradient(135deg, #3B82F6, #1D4ED8)' }
                  ].map((btn, i) => (
                    <motion.button
                      key={i}
                      onClick={btn.fn}
                      className="flex flex-col items-center gap-1 p-2 rounded-xl"
                      style={{ background: btn.bg }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span className={btn.textDark ? 'text-black' : 'text-white'}>{typeof btn.icon === 'string' ? <span className="text-xl">{btn.icon}</span> : btn.icon}</span>
                      <span className={`text-[8px] font-bold ${btn.textDark ? 'text-black' : 'text-white'}`}>{btn.label}</span>
                    </motion.button>
                  ))}
                </div>

                <div className="mt-3 text-center text-xs text-gray-500 bg-black/20 rounded-lg py-2">
                  <Zap className="w-4 h-4 inline text-yellow-500 mr-1" />
                  1 recrue = <span className="text-green-400 font-bold">1 tampon pour toi</span> + <span className="text-cyan-400 font-bold">1 pour lui</span> !
                </div>
              </motion.div>

              {/* TOP RECRUTEURS */}
              <motion.div
                className="relative overflow-hidden rounded-2xl p-4"
                style={{ background: 'linear-gradient(135deg, #020617 0%, #422006 100%)', border: '1px solid rgba(234, 179, 8, 0.2)' }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  <span className="text-sm font-bold text-yellow-400 font-mono">TOP RECRUTEURS</span>
                </div>

                <div className="space-y-2">
                  {topReferrers.slice(0, 5).map((ref, idx) => {
                    const tier = getTier(ref.referralCount);
                    return (
                      <motion.div
                        key={idx}
                        className="flex items-center gap-3 p-2 rounded-xl"
                        style={{
                          background: idx === 0 ? 'rgba(234, 179, 8, 0.15)' : idx === 1 ? 'rgba(156, 163, 175, 0.1)' : idx === 2 ? 'rgba(180, 83, 9, 0.1)' : 'rgba(255,255,255,0.03)'
                        }}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 * idx }}
                      >
                        <div className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs"
                          style={{ background: idx === 0 ? '#FFD700' : idx === 1 ? '#C0C0C0' : idx === 2 ? '#CD7F32' : '#374151', color: idx < 3 ? '#000' : '#fff' }}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 flex items-center gap-2">
                          <span className="text-white font-semibold text-sm">{ref.name}</span>
                          <span className="text-lg">{tier.icon}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-bold" style={{ color: tier.color }}>{ref.referralCount}</span>
                          <span className="text-[10px] text-gray-500 ml-1">recrues</span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>

              {/* PALIERS */}
              <motion.div
                className="relative overflow-hidden rounded-2xl p-4"
                style={{ background: 'linear-gradient(135deg, #020617 0%, #1e1b4b 100%)', border: '1px solid rgba(139, 92, 246, 0.2)' }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Award className="w-5 h-5 text-purple-400" />
                  <span className="text-sm font-bold text-purple-400 font-mono">RANGS & RÉCOMPENSES</span>
                </div>

                <div className="space-y-2">
                  {REFERRAL_TIERS.map((tier) => {
                    const isUnlocked = referralCount >= tier.min;
                    const isCurrent = getTier(referralCount).id === tier.id;
                    return (
                      <div
                        key={tier.id}
                        className={`flex items-center gap-3 p-2 rounded-lg transition-all ${isCurrent ? 'ring-2' : ''}`}
                        style={{ background: isUnlocked ? `${tier.color}15` : 'rgba(0,0,0,0.2)', opacity: isUnlocked ? 1 : 0.5, ringColor: isCurrent ? tier.color : 'transparent' }}
                      >
                        <span className="text-2xl">{tier.icon}</span>
                        <div className="flex-1">
                          <p className="text-sm font-bold" style={{ color: isUnlocked ? tier.color : '#6B7280' }}>{tier.name}</p>
                          <p className="text-[10px] text-gray-500">{tier.min}+ recrues • {tier.benefit}</p>
                        </div>
                        {isUnlocked && <span className="text-green-400">✓</span>}
                      </div>
                    );
                  })}
                </div>
              </motion.div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MODAL PERSONNALISATION */}
      <AnimatePresence>
        {showCustomizeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCustomizeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative w-full max-w-sm rounded-2xl p-6"
              style={{ background: 'linear-gradient(135deg, #020617 0%, #1e1b4b 100%)', border: '2px solid rgba(139, 92, 246, 0.5)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => setShowCustomizeModal(false)} className="absolute top-3 right-3 text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>

              <div className="text-center mb-4">
                <span className="text-4xl">✨</span>
                <h3 className="text-xl font-bold text-white mt-2">Personnalise ton code !</h3>
                <p className="text-sm text-gray-400">Une seule chance 😉</p>
              </div>

              <div className="space-y-4">
                <input
                  type="text"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                  placeholder="Ex: MAXLEFOU"
                  maxLength={15}
                  className="w-full px-4 py-3 rounded-xl text-center text-xl font-bold tracking-widest bg-black/50 text-green-400 border-2 border-purple-500/30 focus:border-purple-500 outline-none"
                />
                <div className="text-xs text-right text-gray-500">{newCode.length}/15</div>

                <div className="text-xs p-3 rounded-lg bg-yellow-500/10 text-yellow-400">⚠️ Tu ne pourras plus le changer !</div>

                <div className="flex gap-2">
                  <Button onClick={() => setShowCustomizeModal(false)} variant="outline" className="flex-1" disabled={isCustomizing}>Annuler</Button>
                  <Button onClick={handleCustomizeCode} className="flex-1 bg-purple-500 hover:bg-purple-600" disabled={isCustomizing || newCode.length < 4}>
                    {isCustomizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Valider</>}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function ReferralMiniWidget({ customer }) {
  const referralCount = customer?.referralCount || 0;
  const currentTier = getTier(referralCount);
  const nextTier = getNextTier(referralCount);

  return (
    <motion.div
      className="flex items-center gap-3 p-3 rounded-xl"
      style={{ background: `linear-gradient(135deg, ${currentTier.color}15 0%, ${currentTier.color}05 100%)`, border: `1px solid ${currentTier.color}30` }}
      whileHover={{ scale: 1.02 }}
    >
      <span className="text-2xl">{currentTier.icon}</span>
      <div className="flex-1">
        <div className="text-xs text-gray-400">{currentTier.name}</div>
        <div className="text-lg font-bold text-white">{referralCount} recrue{referralCount > 1 ? 's' : ''}</div>
      </div>
      {nextTier && (
        <div className="text-right">
          <div className="text-[10px] text-gray-500">Prochain</div>
          <div className="text-sm" style={{ color: nextTier.color }}>{nextTier.icon} {nextTier.min - referralCount}</div>
        </div>
      )}
    </motion.div>
  );
}

export default ReferralShowcase;
